<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcInvoiceGeneratorControls',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({});

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const beforeDestroy = (...args) => {
    poller.value.stop();
  };
  const onPoll = (...args) => {
    console.log('Poll completed!');
  };
  const getPayload = (...args) => {
    {
      {
        return {
          ...item.value,
          ..._.reduce(
            filters.value,
            (m, filter) => {
              const target =
                (filter.op === 'include' ? '' : 'exclude_') + filter.type;
              const arr = _.get(m, target, []);
              arr.push(filter.id);
              m[target] = arr;
              return m;
            },
            {}
          ),
          product_invoice_groups: _.reduce(
            invoiceGroupFilters.value,
            (arr, v, k) => {
              if (!!v) {
                arr.push(k);
              }
              return arr;
            },
            []
          ),
          exclude_product_invoice_groups: _.reduce(
            invoiceGroupFilters.value,
            (arr, v, k) => {
              if (!v) {
                arr.push(k);
              }
              return arr;
            },
            []
          ),
          skip_worksheets: !includeWorksheets.value,
        };
      }
    }
  };
  const updateStatus = (...args) => {
    _.extend(currentGeneratorTask.value, args[0]);
  };

  const labelAlign = ref(null);
  const currentGeneratorTask = ref(null);
  const poller = ref(null);
  const item = ref(null);
  const formId = ref(null);
  const groups = ref(null);
  const productInvoiceGroups = ref(null);
  const contractTypes = ref(null);
  const filterOptionTemplates = ref({
    client: 'UcSuggestCompanyOption',
    contract: 'UcSuggestContractOption',
    rental: 'UcSuggestRentalOption',
  });
  const filterTypes = ref(null);
  const selectedFilterType = ref(null);
  const selectedFilterValue = ref(null);
  const selectedOperation = ref('include');
  const filters = ref(null);
  const includeWorksheets = ref(true);
  const invoiceGroupFilters = ref(null);
  const operations = ref([
    {
      value: 'include',
      text: $fn.value.pgettext('InvoiceGenerator', 'Include'),
    },
    {
      value: 'exclude',
      text: $fn.value.pgettext('InvoiceGenerator', 'Exclude'),
    },
  ]);
  const taskStatusDismissed = ref(false);
  const ux = ref([]);
  const b_5733683578645513_clearable = computed(() => false);

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    currentGeneratorTask: currentGeneratorTask.value,
    poller: poller.value,
    item: item.value,
    formId: formId.value,
    groups: groups.value,
    productInvoiceGroups: productInvoiceGroups.value,
    contractTypes: contractTypes.value,
    filterOptionTemplates: filterOptionTemplates.value,
    filterTypes: filterTypes.value,
    selectedFilterType: selectedFilterType.value,
    selectedFilterValue: selectedFilterValue.value,
    selectedOperation: selectedOperation.value,
    filters: filters.value,
    includeWorksheets: includeWorksheets.value,
    invoiceGroupFilters: invoiceGroupFilters.value,
    operations: operations.value,
    taskStatusDismissed: taskStatusDismissed.value,
    ux: ux.value,
    b_5733683578645513_clearable: b_5733683578645513_clearable.value,
  }));

  const $$load_currentGeneratorTask = async () => {
    return $fn.value.fetch('read_invoice_generation_task', {
      parameters: { id: 'current' },
    });
  };
  const $$load_poller = async () => {
    return $fn.value.poller({
      callback: () => onPoll(),
      id: 'InvoiceGenerator',
    });
  };
  const $$load_item = async () => {
    return {
      description: 'Invoice generation job',
      fulfillment_date: $fn.value.today(),
      due_date: null,
      billing_period_start: $fn.value.today(),
      billing_period_end: null,
      exchange_rates: [],
      service_type_flat_rate: true,
      service_type_measured: true,
      ignore_meter_billing_thresholds: false,
      meter_readings_start_date: null,
      invoice_tags: [],
      include_zero_consumptions: true,
      exclude_past_periods: true,
    };
  };
  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_groups = async () => {
    return $fn.value.fetch('list_group').then((p) => p.data);
  };
  const $$load_productInvoiceGroups = async () => {
    return $fn.value.fetch('list_product_invoice_group');
  };
  const $$load_contractTypes = async () => {
    return _.map($fn.value.getEnumValuesFromSchema('ContractTypes'), (p) => ({
      id: p.value,
      name: p.text,
    }));
  };
  const $$load_filterTypes = async () => {
    return [
      {
        text: $fn.value.gettext('Companies'),
        target: 'companies',
        dataSource: {
          name: 'suggest_company',
          parameters: {
            role: 'own',
            with_contracts: true,
          },
        },
        useOptionTemplateForSelectedOption: true,
        optionTemplate:
          '<UcSuggestCompanyOption :option="option"></UcSuggestCompanyOption>',
      },
      {
        text: $fn.value.gettext('Clients'),
        target: 'tenants',
        dataSource: {
          name: 'suggest_company',
          parameters: {
            role: 'partner',
            with_contracts: true,
          },
        },
        useOptionTemplateForSelectedOption: true,
        optionTemplate:
          '<UcSuggestCompanyOption :option="option"></UcSuggestCompanyOption>',
      },
      {
        text: $fn.value.gettext('Client Groups'),
        target: 'client_groups',
        options: _.filter(groups.value, { group_type: 'company' }),
      },
      {
        text: $fn.value.gettext('Contracts'),
        target: 'contracts',
        dataSource: {
          name: 'suggest_contract',
          parameters: {},
        },
        useOptionTemplateForSelectedOption: true,
        optionTemplate:
          '<UcSuggestContractOption :option="option"></UcSuggestContractOption>',
      },
      {
        text: $fn.value.gettext('Rentals'),
        target: 'rentals',
        dataSource: {
          name: 'suggest_rental',
          parameters: {},
        },
        useOptionTemplateForSelectedOption: true,
        optionTemplate:
          '<UcSuggestRentalOption :option="option"></UcSuggestRentalOption>',
      },
      /*
{
  text: gettext('Product Groups'),
  target: 'product_invoice_groups',
  options: productInvoiceGroups
},
*/
      {
        text: $fn.value.gettext('Meter Groups'),
        target: 'meter_groups',
        options: _.filter(groups.value, { group_type: 'meter' }),
      },
      {
        text: $fn.value.gettext('Contract types'),
        target: 'contract_types',
        options: contractTypes.value,
      },
    ];
  };
  const $$load_filters = async () => {
    return [];
  };
  const $$load_invoiceGroupFilters = async () => {
    return _.chain(productInvoiceGroups.value)
      .reduce((m, p) => ((m[p.id] = true), m), {})
      .value();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'labelAlign' },
      { name: 'currentGeneratorTask' },
      { name: 'poller' },
      {
        name: 'item',
        cache: { type: 'local', key: 'InvoiceGeneratorPanel.formData' },
      },
      { name: 'formId' },
      { name: 'groups' },
      { name: 'productInvoiceGroups' },
      { name: 'contractTypes' },
      { name: 'filterOptionTemplates' },
      {
        name: 'filterTypes',
        dependsOn: ['groups', 'productInvoiceGroups', 'contractTypes'],
      },
      { name: 'selectedFilterType' },
      { name: 'selectedFilterValue' },
      { name: 'selectedOperation' },
      {
        name: 'filters',
        cache: { type: 'local', key: 'InvoiceGeneratorPanel.filters' },
      },
      {
        name: 'includeWorksheets',
        cache: {
          type: 'local',
          key: 'InvoiceGeneratorPanel.includeWorksheets',
        },
      },
      {
        name: 'invoiceGroupFilters',
        cache: {
          type: 'local',
          key: 'InvoiceGeneratorPanel.invoiceGroupFilters',
        },
        dependsOn: ['productInvoiceGroups'],
      },
      { name: 'operations' },
      {
        name: 'taskStatusDismissed',
        cache: {
          type: 'local',
          key: 'InvoiceGeneratorPanel.taskStatusDismissed',
        },
      },
      { name: 'ux' },
      { name: 'b_5733683578645513_clearable', nodeId: '5733683578645513' },
    ],
    loaders: {
      currentGeneratorTask: $$load_currentGeneratorTask,
      poller: $$load_poller,
      item: $$load_item,
      formId: $$load_formId,
      groups: $$load_groups,
      productInvoiceGroups: $$load_productInvoiceGroups,
      contractTypes: $$load_contractTypes,
      filterTypes: $$load_filterTypes,
      filters: $$load_filters,
      invoiceGroupFilters: $$load_invoiceGroupFilters,
    },
    ux: {},
    dataRefs: {
      labelAlign,
      currentGeneratorTask,
      poller,
      item,
      formId,
      groups,
      productInvoiceGroups,
      contractTypes,
      filterOptionTemplates,
      filterTypes,
      selectedFilterType,
      selectedFilterValue,
      selectedOperation,
      filters,
      includeWorksheets,
      invoiceGroupFilters,
      operations,
      taskStatusDismissed,
      ux,
      b_5733683578645513_clearable,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const canAddFilter = computed(() => {
    if (loaded.value) {
      return (
        selectedFilterType.value &&
        selectedFilterValue.value &&
        selectedOperation.value
      );
    }
    return null;
  });
  const canSubmit = computed(() => {
    if (loaded.value) {
      return (
        forms.InvoiceGeneration &&
        forms.InvoiceGeneration.submittable &&
        (!currentGeneratorTask.value ||
          ['PENDING', 'STARTED'].indexOf(currentGeneratorTask.value.status) ==
            -1)
      );
    }
    return null;
  });

  const act_2248226175642056_setData_hv6mctyei_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'taskStatusDismissed',
      value: false,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_setData_jgu8lo3l0_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'currentGeneratorTask',
      value: $event.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_setData_jgu8lo3l0_getWhen = async ($event) => {
    return !!$event.data;
  };
  const act_2248226175642056_crud_gfvztwt7a_getActionArgs = async ($event) => {
    return {
      objectType: 'a_job',
      op: 'upsert',
      data: { ...item.value, job_type: 'invoice_generation', ...getPayload() },
    };
  };
  const act_2646611882120999_script_tj6v4r68o_value_callable = async (
    $event
  ) => {
    return filters.value.push({
      id: selectedFilterValue.value.id,
      type: selectedFilterType.value.target,
      op: selectedOperation.value,
      value: selectedFilterValue.value,
    });
    filters.value = filters.value.slice();
    selectedFilterValue.value = null;
  };
  const act_2646611882120999_script_tj6v4r68o_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2646611882120999_script_tj6v4r68o_value_callable($event);
      },
    };
  };
  const act_8031163192627356_script_8ksidmvsd_value_callable = async (
    $event
  ) => {
    return (
      _.remove(
        filters.value,
        (p) => p.id == $event.scope.filter.id && p.op === $event.scope.filter.op
      ),
      (filters.value = filters.value.slice())
    );
  };
  const act_8031163192627356_script_8ksidmvsd_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_8031163192627356_script_8ksidmvsd_value_callable($event);
      },
    };
  };

  const act_7315092382398562_reloadComponentData_vz4brrbvp =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_maw0mne1l =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_vz4brrbvp,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_maw0mne1l],
    event: 'reload',
  });
  const act_2248226175642056_setData_hv6mctyei = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_hv6mctyei_getActionArgs,
  });
  const inst_evh_2248226175642056_before_xv5y5jkrd =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_hv6mctyei,
      event: 'before',
      displayName: 'setData',
    });
  const act_2248226175642056_setData_jgu8lo3l0 = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_jgu8lo3l0_getActionArgs,
    when: act_2248226175642056_setData_jgu8lo3l0_getWhen,
  });
  const inst_evh_2248226175642056_success_5iulsofpo =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_jgu8lo3l0,
      event: 'success',
      displayName: 'setData',
    });
  const act_2248226175642056_crud_gfvztwt7a = new actionTypes.CRUDAction({
    actionArgs: act_2248226175642056_crud_gfvztwt7a_getActionArgs,
    displayName: 'submit',
    events: [
      inst_evh_2248226175642056_before_xv5y5jkrd,
      inst_evh_2248226175642056_success_5iulsofpo,
    ],
  });
  const inst_evh_2248226175642056_submit_uct2pyak7 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_crud_gfvztwt7a,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_2248226175642056_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_submit_uct2pyak7],
    event: 'submit',
  });
  const act_2646611882120999_script_tj6v4r68o = new actionTypes.ScriptAction({
    actionArgs: act_2646611882120999_script_tj6v4r68o_getActionArgs,
  });
  const inst_evh_2646611882120999_click_gn6dlszi5 =
    new actionTypes.EventHandlerImpl({
      action: act_2646611882120999_script_tj6v4r68o,
      event: 'click',
      displayName: 'script',
    });
  const inst_evh_2646611882120999_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2646611882120999_click_gn6dlszi5],
    event: 'click',
  });
  const act_8031163192627356_script_8ksidmvsd = new actionTypes.ScriptAction({
    actionArgs: act_8031163192627356_script_8ksidmvsd_getActionArgs,
  });
  const inst_evh_8031163192627356_click_wb1pwt7ql =
    new actionTypes.EventHandlerImpl({
      action: act_8031163192627356_script_8ksidmvsd,
      event: 'click',
      displayName: 'script',
    });
  const inst_evh_8031163192627356_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8031163192627356_click_wb1pwt7ql],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_reload_maw0mne1l,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_before_xv5y5jkrd,
    inst_evh_2248226175642056_success_5iulsofpo,
    inst_evh_2248226175642056_submit_uct2pyak7,
    submit: inst_evh_2248226175642056_submit_uct2pyak7,
    inst_evh_2248226175642056_submit,
    inst_evh_2646611882120999_click_gn6dlszi5,
    inst_evh_2646611882120999_click,
    inst_evh_8031163192627356_click_wb1pwt7ql,
    inst_evh_8031163192627356_click,
  };

  const evh_7315092382398562_reload_maw0mne1l = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_maw0mne1l.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_before_xv5y5jkrd = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_before_xv5y5jkrd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_success_5iulsofpo = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_success_5iulsofpo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_submit_uct2pyak7 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_submit_uct2pyak7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_submit = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2646611882120999_click_gn6dlszi5 = async (event, scope) => {
    return await actions.inst_evh_2646611882120999_click_gn6dlszi5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2646611882120999_click = async (event, scope) => {
    return await actions.inst_evh_2646611882120999_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8031163192627356_click_wb1pwt7ql = async (event, scope) => {
    return await actions.inst_evh_8031163192627356_click_wb1pwt7ql.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8031163192627356_click = async (event, scope) => {
    return await actions.inst_evh_8031163192627356_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpForm
      name="InvoiceGeneration"
      schema="CreateInvoiceGenerationTask"
      :readonly="item.locked"
      :id="formId"
      v-if="loaded && item"
      @submit="evh_2248226175642056_submit($event, {})"
    >
      <ItpPanel collapsible stateKey="InvoiceGenerator.panels.filters">
        <template #caption>
          <ItpText
            strong
            muted
            block
            :text="pgettext('InvoiceGenerator', 'Filters')"
          >
          </ItpText>
        </template>
        <ItpBox wrap direction="column" class="mt-3 p-2 w-100">
          <ItpText
            strong
            block
            :text="pgettext('InvoiceGenerator', 'Product Invoice Groups')"
            class="mb-2"
          >
          </ItpText>
          <ItpFormCheckbox
            :name="item.id"
            :key="item.id"
            v-model="invoiceGroupFilters[item.id]"
            :value="invoiceGroupFilters[item.id]"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (invoiceGroupFilters[item.id] =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :label="item.name"
            v-for="item in productInvoiceGroups"
          >
          </ItpFormCheckbox>
          <ItpFormCheckbox
            name="include_worksheet"
            v-model="includeWorksheets"
            :value="includeWorksheets"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (includeWorksheets =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :label="
              pgettext('InvoiceGenerator', 'Include works from worksheets')
            "
          >
          </ItpFormCheckbox>
        </ItpBox>
        <hr />
        <ItpBox wrap direction="column" class="mt-3 p-2 w-100">
          <ItpText
            strong
            block
            :text="pgettext('InvoiceGenerator', 'Other Filters')"
            class="mb-2"
          >
          </ItpText>
          <ItpSelect2
            name="selectedFilterType"
            v-model="selectedFilterType"
            :value="selectedFilterType"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (selectedFilterType =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :options="filterTypes"
            valueField="target"
            textField="text"
            :clearable="b_5733683578645513_clearable"
            bind-object
            class="my-2"
          >
          </ItpSelect2>
          <ItpSelect2
            name="selectedFilterValue"
            v-model="selectedFilterValue"
            :value="selectedFilterValue"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (selectedFilterValue =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            bind-object
            :dataSource="selectedFilterType && selectedFilterType.dataSource"
            :optionTemplate="
              selectedFilterType && selectedFilterType.optionTemplate
            "
            :useOptionTemplateForSelectedOption="
              selectedFilterType &&
              selectedFilterType.useOptionTemplateForSelectedOption
            "
            :options="selectedFilterType && selectedFilterType.options"
            :valueField="
              (selectedFilterType && selectedFilterType.valueField) || 'id'
            "
            :textField="
              (selectedFilterType && selectedFilterType.textField) || 'name'
            "
            textFieldSeparator="-"
            :disabled="!selectedFilterType"
            class="mb-2"
          >
          </ItpSelect2>
          <ItpRow form class="mb-2">
            <ItpCol>
              <ItpFormSelect2
                name="selectedOperation"
                v-model="selectedOperation"
                :value="selectedOperation"
                @input="
                  !($event && typeof $event.stopPropagation === 'function') &&
                  (selectedOperation =
                    $event && typeof $event === 'object' && 'data' in $event
                      ? $event.data
                      : $event)
                "
                :options="operations"
                valueField="value"
                textField="text"
              >
              </ItpFormSelect2>
            </ItpCol>
            <ItpCol>
              <ItpButton
                :title="pgettext('InvoiceGenerator', 'Add Filter')"
                :disabled="!canAddFilter"
                class="ms-auto"
                @click="evh_2646611882120999_click($event, {})"
              >
                <Icon icon="mdi:add-bold"> </Icon>
              </ItpButton>
            </ItpCol>
          </ItpRow>
          <ItpBox wrap direction="column" class="mt-3 w-100">
            <div class="text-center" v-if="!filters.length">
              <ItpText :text="pgettext('InvoiceGenerator', 'No filters')">
              </ItpText>
            </div>
            <template v-for="filter in filters">
              <ItpBox
                :key="filter.id + filter.op"
                spacing="p-2"
                alignItems="baseline"
                class="border rounded"
                v-if="filters.length"
              >
                <ItpIcon
                  :fa="filter.op === 'include' ? 'plus-square' : 'minus-square'"
                  class="px-2"
                >
                </ItpIcon>
                <div
                  v-if="
                    !filter.value.object ||
                    !filterOptionTemplates[filter.value.object]
                  "
                >
                  {{ filter.value.name }}
                </div>
                <component
                  :is="filterOptionTemplates[filter.value.object]"
                  :option="filter.value"
                  v-if="
                    filter.value.object &&
                    filterOptionTemplates[filter.value.object]
                  "
                >
                </component>
                <BCloseButton
                  :title="pgettext('InvoiceGenerator', 'Remove')"
                  class="px-2"
                  @click="evh_8031163192627356_click($event, { filter })"
                >
                </BCloseButton>
              </ItpBox>
            </template>
          </ItpBox>
        </ItpBox>
      </ItpPanel>
      <hr />
      <ItpPanel collapsible stateKey="InvoiceGenerator.panels.parameters">
        <template #caption>
          <ItpText
            strong
            muted
            block
            :text="pgettext('InvoiceGenerator', 'Parameters')"
          >
          </ItpText>
        </template>
        <ItpFormGroup
          name="billing_period_start"
          :label="pgettext('InvoiceGenerator', 'Billing Period Start')"
          :label-cols-md="6"
          :content-cols-md="6"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormDatetime
            name="billing_period_start"
            v-model="item.billing_period_start"
            :placeholder="pgettext('InvoiceGenerator', 'Start of Period')"
            required
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="billing_period_end"
          :label="pgettext('InvoiceGenerator', 'Billing Period End')"
          :label-cols-md="6"
          :content-cols-md="6"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormDatetime
            name="billing_period_end"
            v-model="item.billing_period_end"
            :placeholder="pgettext('InvoiceGenerator', 'End of Period')"
            required
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="invoice_date"
          :label="pgettext('InvoiceGenerator', 'Invoice Date')"
          :label-cols-md="6"
          :content-cols-md="6"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormDatetime
            name="invoice_date"
            v-model="item.invoice_date"
            required
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="fulfillment_date"
          :label="pgettext('InvoiceGenerator', 'Fulfillment Date')"
          :label-cols-md="6"
          :content-cols-md="6"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormDatetime
            name="fulfillment_date"
            v-model="item.fulfillment_date"
            required
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="due_date"
          :label="pgettext('InvoiceGenerator', 'Due Date')"
          :label-cols-md="6"
          :content-cols-md="6"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormDatetime
            name="due_date"
            v-model="item.due_date"
            required
            :placeholder="pgettext('InvoiceGenerator', 'Due Date')"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="meter_readings_start_date"
          :label="pgettext('InvoiceGenerator', 'Meter Readings From')"
          :label-cols-md="6"
          :content-cols-md="6"
          :labelAlign="labelAlign"
        >
          <ItpFormDatetime
            name="meter_readings_start_date"
            v-model="item.meter_readings_start_date"
            :placeholder="pgettext('InvoiceGenerator', 'Meter Readings From')"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="invoice_tags"
          :label="pgettext('InvoiceGenerator', 'Invoice Tags')"
          :label-cols-md="6"
          :content-cols-md="6"
          :labelAlign="labelAlign"
        >
          <ItpSelect2
            name="invoice_tags"
            v-model="item.invoice_tags"
            :value="item.invoice_tags"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.invoice_tags =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            taggable
            multiple
          >
          </ItpSelect2>
        </ItpFormGroup>
      </ItpPanel>
      <hr />
      <ItpPanel collapsible stateKey="InvoiceGenerator.panels.options">
        <template #caption>
          <ItpText
            strong
            muted
            block
            :text="pgettext('InvoiceGenerator', 'Options')"
          >
          </ItpText>
        </template>
        <ItpFormGroup
          :help="
            pgettext(
              'InvoiceGenerator',
              'Do not charge past periods, even if no invoice has been issued.'
            )
          "
          class="mb-0"
        >
          <ItpFormCheckbox
            name="exclude_past_periods"
            v-model="item.exclude_past_periods"
            :value="item.exclude_past_periods"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.exclude_past_periods =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :label="pgettext('InvoiceGenerator', 'Exclude past periods.')"
            required
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <ItpFormGroup class="mb-0">
          <ItpFormCheckbox
            name="include_zero_consumptions"
            v-model="item.include_zero_consumptions"
            :value="item.include_zero_consumptions"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.include_zero_consumptions =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :label="pgettext('InvoiceGenerator', 'Include zero consumptions')"
            required
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <ItpFormGroup
          :help="gettext('Charge all consumptions, even minimal ones.')"
          class="mb-0"
        >
          <ItpFormCheckbox
            name="ignore_meter_billing_thresholds"
            v-model="item.ignore_meter_billing_thresholds"
            :value="item.ignore_meter_billing_thresholds"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.ignore_meter_billing_thresholds =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :label="
              pgettext('InvoiceGenerator', 'Ignore meter billing thresholds.')
            "
            required
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
      </ItpPanel>
      <hr />
      <JobHandlerControl
        jobType="invoice_generation"
        load-current
        :payload="() => getPayload()"
        :text="pgettext('InvoiceGenerator', 'Generate Invoices')"
        icon="mdi:cog"
        :disabled="!canSubmit"
      >
      </JobHandlerControl>
    </ItpForm>
  </ItpUsercontrol>
</template>
